import Pusher from 'pusher-js';
import * as PusherTypes from 'pusher-js';

import { Injectable, Optional } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable()
export class PusherService {

  channels: Map<string, ChannelEntry> = new Map();
  pusher: Pusher;

  private reconnected$ = new Subject<void>();
  private hasConnectedOnce = false;

  /**
   * Emite a cada RECONEXÃO (a primeira conexão é ignorada — as telas já carregam
   * os dados no init). O Pusher **não reentrega** eventos publicados enquanto o
   * cliente estava offline, então sem este sinal a tela ficaria com dados velhos
   * até o próximo evento — que pode nunca vir (bilhete já finalizado).
   */
  get reconnections(): Observable<void> {
    return this.reconnected$.asObservable();
  }

  constructor(@Optional() pusherClient?: Pusher) {
    this.pusher = pusherClient ?? new Pusher(environment.pusher.key, environment.pusher);

    this.pusher.connection.bind('connected', () => {
      if (!this.hasConnectedOnce) {
        this.hasConnectedOnce = true;
        return;
      }
      this.reconnected$.next();
    });
  }

  // ---------------------------------------------------------------------------
  // API legada (mantida para os consumidores existentes: NotificationService,
  // DisplayService etc.). Os handlers registrados por aqui são "legados" — vivem
  // em paralelo aos ownerships de jornada criados por `acquire`.
  // ---------------------------------------------------------------------------

  add(channelName: string, events: Map<string, Function>): void {
    const entry = this.ensureChannel(channelName);

    events.forEach((value: Function, key: string) => {
      entry.bindLegacy(key, value);
    });

  }

  /**
   * Remove a assinatura legada do canal. Handlers com ownership de jornada
   * (`acquire`) NÃO são afetados: se ainda houver algum, o canal continua
   * assinado e fora do `unsubscribe`.
   */
  remove(channelName: string): void {
    const entry = this.channels.get(channelName);

    if (!entry) {
      this.pusher.unsubscribe(channelName);
      return;
    }

    entry.unbindLegacyHandlers();

    if (!entry.isEmpty()) {
      return;
    }

    this.channels.delete(channelName);
    this.pusher.unsubscribe(channelName);
  }

  addHandler(channelName: string, eventName: string, handler: Function): void {
    const entry = this.channels.get(channelName);
    if (entry) {
      entry.bindLegacy(eventName, handler);
    }
  }

  /**
   * Remove apenas o handler legado daquele evento/canal (antes chamava
   * `channel.unbind()` e derrubava TODOS os eventos do canal). Ownerships de
   * jornada (`acquire`) permanecem intactos.
   */
  removeHandler(channelName: string, eventName: string): void {
    const entry = this.channels.get(channelName);
    if (entry) {
      entry.unbindLegacyEvent(eventName);
    }
  }

  exists(channelName: string): boolean {
    return this.channels.has(channelName);
  }

  // ---------------------------------------------------------------------------
  // Ownership aditivo (T600/jornada).
  // ---------------------------------------------------------------------------

  /**
   * Assina UM handler específico no canal/evento informado, sem deduplicar por
   * `eventName` — dois owners do mesmo evento recebem o evento e cada um libera
   * somente o seu próprio par (evento, handler).
   *
   * @returns dispose idempotente. Quando o canal fica sem nenhum handler (legado
   *          nem de jornada), ele é desinscrito e removido do map.
   */
  acquire(channelName: string, eventName: string, handler: Function): () => void {
    const entry = this.ensureChannel(channelName);
    const registration = entry.bindOwnership(eventName, handler);

    let released = false;

    return () => {
      if (released) {
        return;
      }
      released = true;

      registration.unbind();

      if (this.channels.get(channelName) === entry && entry.isEmpty()) {
        this.channels.delete(channelName);
        this.pusher.unsubscribe(channelName);
      }
    };
  }

  private ensureChannel(channelName: string): ChannelEntry {
    let entry = this.channels.get(channelName);

    if (!entry) {
      entry = new ChannelEntry(this.pusher.subscribe(channelName));
      this.channels.set(channelName, entry);
    }

    return entry;
  }
}

/**
 * Estado de um canal assinado. Separa os handlers da API legada dos ownerships
 * de jornada para que cada API libere somente a sua parte — um canal só é
 * desinscrito quando as duas listas estão vazias (ver `PusherService.acquire`).
 */
class ChannelEntry {
  channel: PusherTypes.Channel;

  /** Handlers da API legada, por evento (no máximo um por evento — dedup histórico). */
  private legacyHandlers: Map<string, Function[]> = new Map();

  /** Handlers com ownership de jornada (`acquire`), por evento — sem dedup por evento. */
  private ownershipHandlers: Map<string, Function[]> = new Map();

  constructor(channel: PusherTypes.Channel) {
    this.channel = channel;
  }

  /**
   * Bind legado: mantém o comportamento histórico de ignorar um segundo handler
   * para o mesmo evento (o DisplayService chama `add` repetidas vezes com o
   * mesmo eventMap).
   */
  bindLegacy(eventName: string, handler: Function): void {
    const handlers = this.legacyHandlers.get(eventName) || [];

    if (handlers.length > 0) {
      return;
    }

    this.legacyHandlers.set(eventName, [handler]);
    this.channel.bind(eventName, handler);
  }

  unbindLegacyEvent(eventName: string): void {
    const handlers = this.legacyHandlers.get(eventName) || [];

    handlers.forEach((handler) => this.channel.unbind(eventName, handler));
    this.legacyHandlers.delete(eventName);
  }

  unbindLegacyHandlers(): void {
    Array.from(this.legacyHandlers.keys()).forEach((eventName) => this.unbindLegacyEvent(eventName));
  }

  /**
   * Bind de ownership com wrapper próprio: permite liberar exatamente ESTE
   * acquire mesmo se o mesmo handler for adquirido mais de uma vez no mesmo
   * canal/evento. Nunca usa `unbind_all`, para não derrubar outros owners.
   */
  bindOwnership(eventName: string, handler: Function): OwnershipRegistration {
    const wrapped = (...args: any[]) => handler.apply(undefined, args);
    const handlers = this.ownershipHandlers.get(eventName) || [];

    handlers.push(wrapped);
    this.ownershipHandlers.set(eventName, handlers);
    this.channel.bind(eventName, wrapped);

    return {
      unbind: () => {
        this.channel.unbind(eventName, wrapped);

        const current = this.ownershipHandlers.get(eventName) || [];
        const index = current.indexOf(wrapped);

        if (index >= 0) {
          current.splice(index, 1);
        }

        if (current.length === 0) {
          this.ownershipHandlers.delete(eventName);
        }
      }
    };
  }

  isEmpty(): boolean {
    return this.countHandlers() === 0;
  }

  private countHandlers(): number {
    let total = 0;

    this.legacyHandlers.forEach((handlers) => total += handlers.length);
    this.ownershipHandlers.forEach((handlers) => total += handlers.length);

    return total;
  }
}

interface OwnershipRegistration {
  unbind(): void;
}
